// Detects storage writes made by browser extensions so consent enforcement doesn't block them.

// Chromium family (Chrome, Chromium Edge, Brave, Opera) all use `chrome-extension://`.
// Firefox uses `moz-extension://`; Safari uses `safari-web-extension://`.
// `ms-browser-extension://` is the legacy EdgeHTML
export const EXT_SCHEMES: RegExp =
	/(chrome-extension|moz-extension|safari-web-extension|ms-browser-extension):\/\/([^/)\s]+)/;

// Extension code called into the page code that called us, so its frame is at the bottom of the
// stack — V8's default of 10 truncates it away.
const CAPTURED_FRAME_LIMIT = 50;

export interface ExtensionCaller {
	scheme: string;
	id: string;
}

export interface CallerSignals {
	stack: Error;
	currentScriptSrc: string | null;
}

// V8 only formats the frames when `.stack` is read. Safe on a hot path.
export const captureCallerStack = (): Error => {
	const previousLimit = Error.stackTraceLimit;
	Error.stackTraceLimit = CAPTURED_FRAME_LIMIT;
	const captured = new Error();
	Error.stackTraceLimit = previousLimit;

	return captured;
};

// reading `.stack` formats every retained frame. Slow path only
export const getExtensionCaller = (captured: Error | undefined): ExtensionCaller | null => {
	const match = captured?.stack?.match(EXT_SCHEMES);

	return match ? { scheme: match[1], id: match[2] } : null;
};

/**
 * `src` of the executing script. Null in ES modules and async callbacks, so it only means anything
 * when read at the call site.
 */
export const getCurrentScriptSrc = (): string | null => {
	if (typeof document === 'undefined') {
		return null;
	}

	return (document.currentScript as HTMLScriptElement | null)?.src || null;
};

/**
 * Must be called synchronously from the override, before the consent check — once that resolves the
 * caller's frames have left the stack and `document.currentScript` is null again.
 */
export const captureCallerSignals = (): CallerSignals => ({
	stack: captureCallerStack(),
	currentScriptSrc: getCurrentScriptSrc(),
});

/**
 * Neither signal is enough alone: the stack catches inline `<script>` injection but loses the
 * extension frames once the write happens in an async callback, which is where `currentScript` still
 * identifies an injected `<script src="chrome-extension://…">`. A `postMessage` bridge, where page
 * code does the write on the extension's behalf, is a blind spot for both.
 *
 * Undefined signals mean detection is gated off.
 */
export const isExtensionCaller = (signals: CallerSignals | undefined): boolean => {
	if (!signals) {
		return false;
	}

	const stackMatch = getExtensionCaller(signals.stack) !== null;
	const currentScriptMatch =
		signals.currentScriptSrc !== null && EXT_SCHEMES.test(signals.currentScriptSrc);
	const detected = stackMatch || currentScriptMatch;

	return detected;
};
